/**
 * App shell: consume any magic-link callback, work out who is signed in, and
 * mount the right view. Routing is plain History API against the SPA fallback.
 */
import './styles.css';

import { consumeMagicLinkCallback, getCurrentUser, signOut } from './auth';
import type { CurrentUser } from './auth';
import { messageOf } from './api';
import { announce, el, onClickAsync, setDocumentTitle } from './ui';
import { mountEnvelope } from './views/envelope';
import { mountInbox } from './views/inbox';
import { mountSignIn } from './views/sign-in';
import type { Teardown, ViewContext } from './views/view';

const view = document.getElementById('view');
const sessionArea = document.getElementById('session-area');

if (!(view instanceof HTMLElement)) {
  throw new Error('The page is missing its #view container.');
}
const viewHost = view;

/** Teardown for whatever view is currently mounted. */
let unmount: Teardown | null = null;
/** Guards against two overlapping route() calls racing each other. */
let routeToken = 0;

interface RouteOptions {
  authError?: string;
  focusView?: boolean;
}

function teardown(): void {
  if (!unmount) return;
  const run = unmount;
  unmount = null;
  run();
}

/* -------------------------------------------------------------------------- */
/* Header                                                                     */
/* -------------------------------------------------------------------------- */

function renderSession(user: CurrentUser | null): void {
  if (!(sessionArea instanceof HTMLElement)) return;
  if (!user) {
    sessionArea.replaceChildren();
    return;
  }

  const out = el('button', {
    class: 'button button-quiet button-small',
    text: 'Sign out',
    attrs: { type: 'button' },
  });

  onClickAsync(out, 'Signing out...', async () => {
    try {
      await signOut();
    } catch {
      // The session is unusable either way; fall through to a re-route.
    }
    announce('Signed out.');
    window.history.pushState(null, '', '/');
    await route({ focusView: true });
  });

  sessionArea.replaceChildren(
    el('span', { class: 'session-email', text: user.email, attrs: { title: user.email } }),
    out,
  );
}

/* -------------------------------------------------------------------------- */
/* Routing                                                                    */
/* -------------------------------------------------------------------------- */

function notFound(navigate: (path: string) => void): Teardown {
  setDocumentTitle('Not found');
  viewHost.replaceChildren(
    el('section', {
      class: 'card card-narrow',
      children: [
        el('h1', { class: 'title', text: 'That page does not exist' }),
        el('p', { class: 'lede', text: 'The link may be out of date or mistyped.' }),
        el('button', {
          class: 'button button-primary',
          text: 'Go to your documents',
          attrs: { type: 'button' },
          on: { click: () => navigate('/') },
        }),
      ],
    }),
  );
  return () => undefined;
}

/** The only deep route this app serves: a single document. */
const ENVELOPE_ROUTE = /^\/e\/([^/]+)$/;

/** The requested route, with any trailing slashes trimmed off. */
function currentPath(): string {
  return window.location.pathname.replace(/\/+$/, '') || '/';
}

/**
 * Where a sign-in link should land. Someone who followed a document link while
 * signed out gets sent back to that document, not to the inbox. Unknown paths
 * fall back to the inbox rather than mailing out a link to a "not found" page.
 */
function returnPathForSignIn(): string {
  const path = currentPath();
  return ENVELOPE_ROUTE.test(path) ? path : '/';
}

function navigate(path: string): void {
  if (path !== `${window.location.pathname}${window.location.search}`) {
    window.history.pushState(null, '', path);
  }
  void route({ focusView: true });
}

async function route(options: RouteOptions = {}): Promise<void> {
  const token = ++routeToken;
  teardown();

  const user = await getCurrentUser();
  if (token !== routeToken) return; // a newer route() took over

  renderSession(user);

  const ctx: ViewContext = {
    user,
    navigate,
    refresh: () => {
      void route();
    },
  };

  if (!user) {
    unmount = mountSignIn(viewHost, options.authError ?? null, returnPathForSignIn());
  } else {
    const path = currentPath();
    const envelopeId = ENVELOPE_ROUTE.exec(path)?.[1];

    if (path === '/') unmount = mountInbox(viewHost, ctx);
    else if (envelopeId) unmount = mountEnvelope(viewHost, ctx, decodeURIComponent(envelopeId));
    else unmount = notFound(navigate);
  }

  if (options.focusView) viewHost.focus();
}

/* -------------------------------------------------------------------------- */
/* Wiring                                                                     */
/* -------------------------------------------------------------------------- */

// Intercept in-app links so navigation never round-trips the server.
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  const link = target.closest('a[data-link]');
  if (!(link instanceof HTMLAnchorElement)) return;
  if (link.origin !== window.location.origin) return;
  event.preventDefault();
  navigate(`${link.pathname}${link.search}`);
});

window.addEventListener('popstate', () => {
  void route();
});

/** Last line of defence: nothing should ever reach here, but never go silent. */
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled rejection', event.reason);
});

async function boot(): Promise<void> {
  const callback = await consumeMagicLinkCallback();
  if (callback.kind === 'signed-in') {
    announce('Signed in.');
    await route();
    return;
  }
  if (callback.kind === 'failed') {
    await route({ authError: callback.message });
    return;
  }
  await route();
}

void boot().catch((err: unknown) => {
  viewHost.replaceChildren(
    el('section', {
      class: 'card card-narrow',
      children: [
        el('h1', { class: 'title', text: 'Something went wrong' }),
        el('p', {
          class: 'notice notice-error',
          text: messageOf(err, 'The app could not start. Please reload the page.'),
          attrs: { role: 'alert' },
        }),
        el('button', {
          class: 'button button-primary',
          text: 'Reload',
          attrs: { type: 'button' },
          on: { click: () => window.location.reload() },
        }),
      ],
    }),
  );
});
